import {
    useEffect,
    useState,
    Fragment
} from "react";
import {
    type LayoutChangeEvent,
    Platform,
    View
} from "react-native";
import type IHeaderProps from "./type";
import type {
    HeaderWebPropsType,
    NavigationType
} from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    type NCoreUIKitIcon
} from "../../types";
import {
    SafeAreaView
} from "react-native-safe-area-context";
import {
    ArrowLeftIcon
} from "lucide-react-native";
import {
    warnOnce
} from "../../utils/development";
import AnimatedIcon from "../animatedIcon";
import Button from "../button";
import Text from "../text";

const HeaderBackIcon: NCoreUIKitIcon = ({
    customColor,
    color,
    size
}) => <AnimatedIcon
    customColor={customColor}
    animationTrigger="hover"
    animation="signature"
    icon={ArrowLeftIcon}
    color={color}
    size={size}
/>;

const Header = <T extends NavigationType>({
    backgroundColor = "default" as keyof NCoreUIKit.ContainerContentColors,
    isWrapSafeAreaContext: isWrapSafeAreaContextProp,
    isWorkWithHeaderSpace = true,
    renderRight: renderRightProp,
    renderTitle: renderTitleProp,
    safeAreaViewBackgroundColor,
    renderLeft: renderLeftProp,
    isWrapSafeareaContext,
    safeAreaViewStyle,
    isGoBackEnabled,
    customLocalize,
    isGoBackEnable,
    customTheme,
    headerSpace,
    isScrolled,
    navigation,
    titleProps,
    children,
    actions,
    style,
    title,
    ...props
}: IHeaderProps<T>) => {
    const {
        controlHeights,
        shapeConfigs,
        shadowAlpha,
        elevations,
        borders,
        configs,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        direction,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const [
        startWidth,
        setStartWidth
    ] = useState(0);

    const [
        endWidth,
        setEndWidth
    ] = useState(0);

    const isWrapWithSafeAreaView = (isWrapSafeAreaContextProp ?? isWrapSafeareaContext ?? true) && !isWorkWithHeaderSpace;

    const isGoBackVisible = !!(isGoBackEnabled ?? isGoBackEnable) && !!navigation && navigation.canGoBack();

    const {
        safeAreaView: safeAreaViewDynamicStyle,
        slotContent: slotContentDynamicStyle,
        container: containerDynamicStyle,
        sideSlot: sideSlotDynamicStyle
    } = useStyles({
        sideMinWidth: Math.max(startWidth, endWidth),
        safeAreaViewBackgroundColor,
        isWorkWithHeaderSpace,
        backgroundColor,
        controlHeights,
        shapeConfigs,
        shadowAlpha,
        headerSpace,
        elevations,
        isScrolled,
        direction,
        borders,
        configs,
        colors,
        spaces
    });

    const headerWebProps: HeaderWebPropsType = Platform.OS === "web" ? {
        dir: direction
    } : {};

    useEffect(() => {
        if(isGoBackEnable === undefined) {
            return;
        }

        warnOnce({
            message: "Header: \"isGoBackEnable\" is deprecated and will be removed in the next major version. Use \"isGoBackEnabled\" instead."
        });
    }, [
        isGoBackEnable
    ]);

    useEffect(() => {
        if(isWrapSafeareaContext === undefined) {
            return;
        }

        warnOnce({
            message: "Header: \"isWrapSafeareaContext\" is deprecated and will be removed in the next major version. Use \"isWrapSafeAreaContext\" instead."
        });
    }, [
        isWrapSafeareaContext
    ]);

    const onStartLayout = (event: LayoutChangeEvent) => {
        setStartWidth(event.nativeEvent.layout.width);
    };

    const onEndLayout = (event: LayoutChangeEvent) => {
        setEndWidth(event.nativeEvent.layout.width);
    };

    const onGoBackPress = () => {
        navigation?.goBack();
    };

    const renderGoBack = () => {
        if(!isGoBackVisible) {
            return null;
        }

        return <Button
            accessibilityLabel={localize("go-back")}
            customTheme={customTheme}
            onPress={onGoBackPress}
            spreadBehaviour="free"
            icon={HeaderBackIcon}
            variant="ghost"
            type="neutral"
            size="large"
        />;
    };

    const renderActions = () => {
        if(!actions || !actions.length) {
            return null;
        }

        return actions.map((actionItem, index) => {
            return <Button
                key={actionItem.id ?? index}
                customTheme={customTheme}
                spreadBehaviour="free"
                variant="ghost"
                type="neutral"
                size="large"
                {...actionItem}
            />;
        });
    };

    const renderStartContent = () => {
        if(renderLeftProp) {
            return renderLeftProp({
                navigation
            });
        }

        return renderGoBack();
    };

    const renderEndContent = () => {
        if(renderRightProp) {
            return renderRightProp({
                navigation
            });
        }

        return renderActions();
    };

    const renderTitle = () => {
        if(renderTitleProp) {
            return renderTitleProp({
                navigation
            });
        }

        if(!title) {
            return null;
        }

        return <Text
            customTheme={customTheme}
            variant="titleLargeSize"
            ellipsizeMode="tail"
            numberOfLines={1}
            role="heading"
            color="high"
            {...titleProps}
            style={[
                stylesheet.title,
                titleProps?.style
            ]}
        >
            {title}
        </Text>;
    };

    const renderContent = () => {
        return <View
            {...props}
            {...headerWebProps}
            style={[
                stylesheet.container,
                containerDynamicStyle,
                style
            ]}
        >
            <View
                style={[
                    stylesheet.sideSlot,
                    stylesheet.startSlot,
                    sideSlotDynamicStyle
                ]}
            >
                <View
                    style={[
                        stylesheet.slotContent,
                        slotContentDynamicStyle
                    ]}
                    onLayout={onStartLayout}
                >
                    {renderStartContent()}
                </View>
            </View>
            <View
                style={[
                    stylesheet.titleSlot
                ]}
            >
                {renderTitle()}
            </View>
            <View
                style={[
                    stylesheet.sideSlot,
                    stylesheet.endSlot,
                    sideSlotDynamicStyle
                ]}
            >
                <View
                    style={[
                        stylesheet.slotContent,
                        slotContentDynamicStyle
                    ]}
                    onLayout={onEndLayout}
                >
                    {renderEndContent()}
                </View>
            </View>
        </View>;
    };

    const renderWithSafeAreaView = () => {
        return <SafeAreaView
            style={[
                safeAreaViewStyle,
                safeAreaViewDynamicStyle
            ]}
            edges={[
                "top"
            ]}
        >
            {children ? children : renderContent()}
        </SafeAreaView>;
    };

    const renderSafeAreaContext = () => {
        return isWrapWithSafeAreaView ? renderWithSafeAreaView() : <Fragment>
            {children ? children : renderContent()}
        </Fragment>;
    };

    return renderSafeAreaContext();
};
export default Header;
